import { useState, useEffect, useMemo } from "react";
import axiosClient from "@service/axiosClient";
import { fetchPTORequestsForWorkerPeriod } from "@service/ptoService";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
} from "date-fns";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import UserCalendarView from "./UserCalendarView";
import UserTableView from "./UserTableView";

interface UserHoursPageNewProps {
  workerId: number;
  onOpenPTOBalance?: () => void;
}

interface WorkedHours {
  id: number;
  worker_id: number;
  date: string;
  hours: number;
  timesheet_id?: number;
  is_holiday?: boolean;
  flat_amount?: number;
  gifted_hours?: number;
  created_at?: string;
  updated_at?: string;
}

interface PTORequest {
  id: number;
  worker_id: number;
  start_date: string;
  end_date: string;
  hours_requested: number;
  status: string;
  request_type: string;
  description?: string;
  request_date?: string;
  pto_balance_at_request?: number;
  pto_balance_after?: number;
  status_reason?: string;
}

export default function UserHoursPageNew({
  workerId,
  onOpenPTOBalance,
}: UserHoursPageNewProps) {
  const { t, i18n } = useTranslation();
  const [viewMode, setViewMode] = useState("table");
  const [workedHours, setWorkedHours] = useState<WorkedHours[]>([]);
  const [ptoRequests, setPtoRequests] = useState<PTORequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Date filter state - calculate first day of previous week and last day of that month
  const getInitialDates = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const firstDayOfWeek = new Date(today);
    // Go to Sunday of current week, then subtract 7 days for previous week
    firstDayOfWeek.setDate(today.getDate() - dayOfWeek - 7);

    // Get first day of the month from previous week
    const firstDayOfMonth = new Date(
      firstDayOfWeek.getFullYear(),
      firstDayOfWeek.getMonth(),
      1
    );

    const lastDayOfMonth = new Date(
      firstDayOfMonth.getFullYear(),
      firstDayOfMonth.getMonth() + 1,
      0
    );
    return {
      start: firstDayOfMonth.toISOString().split("T")[0],
      end: lastDayOfMonth.toISOString().split("T")[0],
    };
  };

  const initialDates = getInitialDates();
  const [dateFilterStart, setDateFilterStart] = useState(initialDates.start);
  const [dateFilterEnd, setDateFilterEnd] = useState(initialDates.end);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch worked hours data
  useEffect(() => {
    const fetchWorkedHours = async () => {
      if (!workerId) return;

      setIsLoading(true);
      setError(null);

      try {
        // Calculate Sunday for start date (go back to Sunday if not Sunday)
        const startDate = new Date(dateFilterStart + "T00:00:00");
        const startDayOfWeek = startDate.getDay(); // 0 = Sunday
        const adjustedStartDate = new Date(startDate);
        if (startDayOfWeek !== 0) {
          // Go back to the previous Sunday
          adjustedStartDate.setDate(startDate.getDate() - startDayOfWeek);
        }

        // Calculate Saturday for end date (go forward to Saturday if not Saturday)
        const endDate = new Date(dateFilterEnd + "T00:00:00");
        const endDayOfWeek = endDate.getDay(); // 6 = Saturday
        const adjustedEndDate = new Date(endDate);
        if (endDayOfWeek !== 6) {
          // Go forward to the next Saturday
          adjustedEndDate.setDate(endDate.getDate() + (6 - endDayOfWeek));
        }

        const params: any = {
          worker_id: workerId,
          start_date: adjustedStartDate.toISOString().split("T")[0],
          end_date: adjustedEndDate.toISOString().split("T")[0],
        };

        const response = await axiosClient.get(
          "/worked_hours_api/worked-hours",
          {
            params,
          }
        );

        setWorkedHours(response.data || []);

        // Fetch PTO requests for the same period
        try {
          const ptoResponse = await fetchPTORequestsForWorkerPeriod(
            workerId,
            adjustedStartDate.toISOString().split("T")[0],
            adjustedEndDate.toISOString().split("T")[0]
          );
          setPtoRequests(ptoResponse.requests || []);
        } catch (ptoErr: any) {
          console.error("Error fetching PTO requests:", ptoErr);
          // Don't set error state, just log - PTO fetch failure shouldn't prevent showing worked hours
        }
      } catch (err: any) {
        console.error("Error fetching worked hours:", err);
        setError(err.message || "Failed to fetch worked hours");
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchWorkedHours();
  }, [workerId, dateFilterStart, dateFilterEnd]);

  // Calendar logic - derive current month from dateFilterStart
  const currentMonth = useMemo(() => {
    return new Date(dateFilterStart + "T00:00:00");
  }, [dateFilterStart]);

  const monthMatrix = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
    const days = [];
    let day = start;
    while (day <= end) {
      days.push(day);
      day = addDays(day, 1);
    }
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  }, [currentMonth]);

  const dayEvents = useMemo(() => {
    const events: { [key: string]: (WorkedHours | PTORequest)[] } = {};

    // Add worked hours
    workedHours.forEach((wh) => {
      if (!events[wh.date]) {
        events[wh.date] = [];
      }
      events[wh.date].push(wh);
    });

    // Add PTO requests - expand multi-day requests into individual days (including Sundays)
    ptoRequests.forEach((pto) => {
      const startDate = new Date(pto.start_date + "T00:00:00");
      const endDate = new Date(pto.end_date + "T00:00:00");

      // Iterate through each day in the PTO request range (including Sundays)
      for (
        let d = new Date(startDate);
        d <= endDate;
        d.setDate(d.getDate() + 1)
      ) {
        const dateStr = d.toISOString().split("T")[0];
        if (!events[dateStr]) {
          events[dateStr] = [];
        }
        events[dateStr].push(pto);
      }
    });

    return events;
  }, [workedHours, ptoRequests]);

  // Date filter navigation
  const handlePrevFilterMonth = () => {
    const [year, month] = dateFilterStart.split("-").map(Number);
    // month is 1-12 in ISO format, Date constructor uses 0-11
    // So month-1 in Date constructor gives us current month, month-2 gives previous month
    const prevMonthFirstDay = new Date(year, month - 2, 1);
    const prevMonthLastDay = new Date(year, month - 1, 0);
    setDateFilterStart(prevMonthFirstDay.toISOString().split("T")[0]);
    setDateFilterEnd(prevMonthLastDay.toISOString().split("T")[0]);
  };

  const handleNextFilterMonth = () => {
    const [year, month] = dateFilterStart.split("-").map(Number);
    // month is 1-12 in ISO format, Date constructor uses 0-11
    // So month in Date constructor gives us next month
    const nextMonthFirstDay = new Date(year, month, 1);
    const nextMonthLastDay = new Date(year, month + 1, 0);
    setDateFilterStart(nextMonthFirstDay.toISOString().split("T")[0]);
    setDateFilterEnd(nextMonthLastDay.toISOString().split("T")[0]);
  };

  const filterMonthDisplay = useMemo(() => {
    const date = new Date(dateFilterStart + "T00:00:00");
    const locale = i18n.language === "es" ? "es-ES" : "en-US";
    return date.toLocaleDateString(locale, {
      month: "long",
      year: "numeric",
    });
  }, [dateFilterStart, i18n.language]);

  // Prepare table data with week numbers
  const tableData = useMemo(() => {
    const data: any[] = [];
    const locale = i18n.language === "es" ? "es-ES" : "en-US";

    const getWeekNumber = (date: Date) => {
      // Week starts on Sunday (day 0)
      // Create a date copy at midnight UTC to avoid timezone issues
      const d = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      );
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));

      // Calculate the day of week for Jan 1 (0 = Sunday, 6 = Saturday)
      const yearStartDay = yearStart.getUTCDay();

      // Calculate day of year (1-based)
      const dayOfYear =
        Math.floor((d.getTime() - yearStart.getTime()) / 86400000) + 1;

      // Calculate first Sunday of the year
      // If Jan 1 is Sunday, firstSundayDay = 1
      // If Jan 1 is Monday (1), firstSundayDay = 7
      // If Jan 1 is Saturday (6), firstSundayDay = 2
      const firstSundayDay = yearStartDay === 0 ? 1 : 8 - yearStartDay;

      // Week 1 includes everything from Jan 1 through the first Saturday after the first Sunday
      // That's days 1 through (firstSundayDay + 6)
      const firstWeekEndDay = firstSundayDay + 6;

      if (dayOfYear <= firstWeekEndDay) {
        return 1;
      } else {
        // Calculate weeks after week 1
        const daysAfterWeek1 = dayOfYear - firstWeekEndDay;
        return Math.floor((daysAfterWeek1 - 1) / 7) + 2;
      }
    };

    // Add worked hours
    workedHours.forEach((wh) => {
      const date = new Date(wh.date + "T00:00:00");
      const weekNumber = getWeekNumber(date);
      const month = date.toLocaleDateString(locale, { month: "long" });
      const dayOfWeek = date.toLocaleDateString(locale, { weekday: "long" });

      data.push({
        id: `wh-${wh.id}`,
        date: wh.date,
        weekNumber,
        month,
        dayOfWeek,
        hours: wh.hours,
        timesheetId: wh.timesheet_id,
        isHoliday: wh.is_holiday || false,
        flatAmount: wh.flat_amount,
        giftedHours: wh.gifted_hours,
        type: "worked",
      });
    });

    // Add PTO requests - expand multi-day requests into individual days (including Sundays)
    ptoRequests.forEach((pto) => {
      const startDate = new Date(pto.start_date + "T00:00:00");
      const endDate = new Date(pto.end_date + "T00:00:00");

      // Calculate hours per day (distribute across all days including Sundays)
      let workDays = 0;
      for (
        let d = new Date(startDate);
        d <= endDate;
        d.setDate(d.getDate() + 1)
      ) {
        workDays++;
      }
      const hoursPerDay = workDays > 0 ? pto.hours_requested / workDays : 0;

      // Iterate through each day in the PTO request range (including Sundays)
      for (
        let d = new Date(startDate);
        d <= endDate;
        d.setDate(d.getDate() + 1)
      ) {
        const dateStr = d.toISOString().split("T")[0];
        const date = new Date(dateStr + "T00:00:00");
        const weekNumber = getWeekNumber(date);
        const month = date.toLocaleDateString(locale, { month: "long" });
        const dayOfWeek = date.toLocaleDateString(locale, { weekday: "long" });

        data.push({
          id: `pto-${pto.id}-${dateStr}`,
          date: dateStr,
          weekNumber,
          month,
          dayOfWeek,
          hours: hoursPerDay,
          ptoRequestId: pto.id,
          ptoStatus: pto.status,
          ptoType: pto.request_type,
          ptoDescription: pto.description,
          type: "pto",
          isHoliday: false,
        });
      }
    });

    const sortedData = data.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    return sortedData;
  }, [workedHours, ptoRequests, i18n.language]);

  return (
    <div className="tw-p-1 tw-ml-2 tw-mr-5 tw-mt-2">
      {error && (
        <div className="tw-bg-red-50 tw-text-red-700 tw-border tw-border-red-200 tw-rounded tw-p-3 tw-mb-4">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="tw-bg-white dark:tw-bg-[#0D1117] tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-rounded-lg tw-p-2 tw-mb-2">
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-between tw-gap-1">
          {/* View Mode Toggle - Right side */}
          <div className="tw-flex tw-gap-2 tw-mr-2">
            {/* Statement Summary Section */}
            <div className="tw-flex tw-gap-2">
              <Button
                component={Link}
                to={`/contractor/${workerId}/statement-history`}
                variant="contained"
                startIcon={<HistoryOutlinedIcon />}
                sx={{ fontSize: { xs: "0.65rem", md: "0.875rem" } }}
              >
                {t("userProfile.history")}
              </Button>
              <Button
                onClick={onOpenPTOBalance}
                variant="contained"
                color="primary"
                startIcon={<AccountBalanceWalletIcon />}
                sx={{ fontSize: { xs: "0.60rem", md: "0.875rem" } }}
              >
                {t("userProfile.ptoBalance")}
              </Button>
            </div>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                setViewMode(viewMode === "table" ? "calendar" : "table")
              }
              sx={{ fontSize: { xs: "0.60rem", md: "0.875rem" } }}
            >
              {viewMode === "table"
                ? t("userProfile.calendar")
                : t("userProfile.table")}
            </Button>
          </div>
          {/* Spacer for left side */}
          <div className="tw-flex tw-items-center">
            <button
              onClick={handlePrevFilterMonth}
              className="tw-p-2 tw-border tw-rounded-md"
              aria-label={t("userProfile.previousMonth")}
              disabled={isLoading}
            >
              <ChevronLeft className="tw-w-4 tw-h-4" />
            </button>
            <div className="tw-font-medium tw-min-w-40 tw-text-center">
              {filterMonthDisplay}
            </div>
            <button
              onClick={handleNextFilterMonth}
              className="tw-p-2 tw-border tw-rounded-md"
              aria-label={t("userProfile.nextMonth")}
              disabled={isLoading}
            >
              <ChevronRight className="tw-w-4 tw-h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Table View */}
      {viewMode === "table" && (
        <UserTableView
          tableData={tableData}
          isInitialLoad={isInitialLoad}
          isLoading={isLoading}
          isMobile={isMobile}
          t={t}
        />
      )}

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <UserCalendarView
          monthMatrix={monthMatrix}
          dayEvents={dayEvents}
          currentMonth={currentMonth}
          isLoading={isLoading}
          isInitialLoad={isInitialLoad}
        />
      )}
    </div>
  );
}
